import React, { useEffect } from 'react';
import { observer } from 'mobx-react';

import { useClientStore } from 'apps/hooks';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { DfmStatus } from 'libs/rendering/meshAnalysis/appManager';
import {
  getFeatureIcon,
  getSurfaceLabel,
  getSurfaceIcon,
  getSurfaceLabelColor,
} from 'apps/rfq/dfmConfig';

import {
  AccordionItem,
  Heading,
  Box,
  SvgIcon,
  Divisor,
  Icon,
} from 'components';

import { InfoIcon, Close } from 'icons';

import {
  Navigation,
  RowWrapper,
  NavItem,
  NameWrapper,
} from 'libs/rendering/gltf/components/geometry/geometry.styled';

import { Feature } from './Feature';
import { Face } from './Face';
import { WallOffsetPair } from './WallOffsetPair';

import { UiModes } from '../../config';

import { FeaturesViewportWrapper } from './manufacturingFeatures.styled';

class BodyFeatureGroup {
  body = null;
  type = null; // [enum TopoFeatureType]
  featEnum = null;
  features = []; // [MeshFeature*]
  offsetPairs = [];

  constructor(body, type, featEnum) {
    this.body = body;
    this.type = type;
    this.featEnum = featEnum;
    if (body && type && featEnum) {
      if (body.isReadyForDfmCutoff) {
        const dfm = body.GetDfm();
        if (dfm) {
          if (type !== -1) {
            // topo features (vector<MeshFeature*>)
            const featsVect = dfm.GetFeatures(type);
            const n = featsVect?.size();
            for (let i = 0; i < n; i++) {
              const featObj = featsVect.get(i);
              this.features.push(featObj);
            }

            // sort by extent
            this.features.sort((a, b) => {
              const extA = a.GetExtent();
              const extB = b.GetExtent();
              const maxA = extA.y < 0 ? extA.x : extA.y;
              const maxB = extB.y < 0 ? extB.x : extB.y;
              if (maxA === maxB) return 0;
              if (maxA > maxB) return 1; // a after b
              return -1; // b after a
            });
          } else {
            // wall offset pairs (vector<WallOffsetPair*>)
            const offsetPairsKeys = dfm.GetWallOffsetPairsKeys();
            const n = offsetPairsKeys.size();
            for (let i = 0; i < n; i++) {
              const offsetPairKey = offsetPairsKeys.get(i);
              const offsetPairObj = dfm.GetFaceOffsetPair(offsetPairKey);
              this.offsetPairs.push(offsetPairObj);
            }

            // sort by offset
            this.offsetPairs.sort((a, b) => {
              const diff = Math.round(100 * (a.offset - b.offset)) / 100; // b - a for reverse sort
              const diff2 = Math.round(100 * (a.minOffset - b.minOffset)) / 100;
              const diff3 = a.type.value - b.type.value;
              return diff !== 0 ? diff : diff2 !== 0 ? diff2 : diff3;
            });
          }
        }
      }
    }
  }

  get featCount() {
    return this.features.length;
  }

  get offsetPairCount() {
    return this.offsetPairs.length;
  }

  get isEmpty() {
    return this.featCount === 0 && this.offsetPairCount === 0;
  }

  get title() {
    switch (this.type) {
      case -1:
        return 'Offset Pairs';
      case this.featEnum.TopoFeatureHole:
        return 'Hole';
      case this.featEnum.TopoFeatureSlot:
        return 'Slot';
      case this.featEnum.TopoFeatureBoss:
        return 'Boss';
      case this.featEnum.TopoFeatureGusset:
        return 'Gusset';
      case this.featEnum.TopoFeatureNotch:
        return 'Notch';
      case this.featEnum.TopoFeatureCavity:
        return 'Cavity';
      case this.featEnum.TopoFeaturePocket:
        return 'Pocket';
      default:
        break;
    }
    return null;
  }
}

class BodyGeometry {
  body = null;
  surfEnum = null;
  faces = new Map();

  constructor(body, surfEnum) {
    this.body = body;
    this.surfEnum = surfEnum;
    if (body && surfEnum) {
      if (body.isReadyForDfmCutoff) {
        const wasmObj = body.isFaceted
          ? body.GetBodyMesh()?.GetMeshFrame()
          : body.GetBrep();

        if (wasmObj) {
          const n = wasmObj.faceCount;
          for (let i = 0; i < n; i++) {
            const face = wasmObj.GetFace(i);
            if (face) {
              const faceJson = JSON.parse(face.GetSurfaceData());
              if (this.faces.has(faceJson.type)) {
                this.faces.get(faceJson.type).push(face);
              } else {
                this.faces.set(faceJson.type, [face]);
              }
            }
          }

          // order
          this.faces.forEach((faces, surfType) => {
            if (surfType === surfEnum.SurfaceTypeSphere.value) {
              // SPHERE
              faces.sort((a, b) => {
                // sort by radius
                const aJson = JSON.parse(a.GetSurfaceData());
                const bJson = JSON.parse(b.GetSurfaceData());
                const diff = bJson.radius - aJson.radius;
                return diff;
              });
            } else if (surfType === surfEnum.SurfaceTypeCylinder.value) {
              // CYLINDER
              faces.sort((a, b) => {
                // sort by radius, then by height
                const aJson = JSON.parse(a.GetSurfaceData());
                const bJson = JSON.parse(b.GetSurfaceData());
                const diff =
                  Math.round(100 * (bJson.radius - aJson.radius)) / 100;
                const diff2 =
                  Math.round(100 * (bJson.heigh - aJson.heigh)) / 100;
                return diff !== 0 ? diff : diff2;
              });
            } else if (surfType === surfEnum.SurfaceTypeTorus.value) {
              // TORUS
              faces.sort((a, b) => {
                // sort by radius, then by inner-radius
                const aJson = JSON.parse(a.GetSurfaceData());
                const bJson = JSON.parse(b.GetSurfaceData());
                const diff =
                  Math.round(100 * (bJson.radius - aJson.radius)) / 100;
                const diff2 =
                  Math.round(100 * (bJson.circle.r - aJson.circle.r)) / 100;
                return diff !== 0 ? diff : diff2;
              });
            } else if (surfType === surfEnum.SurfaceTypeCone.value) {
              // CONE
              faces.sort((a, b) => {
                // sort by radius, then by height, then by angle
                const aJson = JSON.parse(a.GetSurfaceData());
                const bJson = JSON.parse(b.GetSurfaceData());
                const diff =
                  Math.round(100 * (bJson.radius - aJson.radius)) / 100;
                const diff2 =
                  Math.round(100 * (bJson.heigh - aJson.heigh)) / 100;
                const diff3 =
                  Math.round(100 * (bJson.angle - aJson.angle)) / 100;
                return diff !== 0 ? diff : diff2 !== 0 ? diff2 : diff3;
              });
            } else {
              faces.sort((a, b) => {
                // PLANE, NURBS, UNKNOWN...
                // sort by area
                const aS = a.GetArea();
                const bS = b.GetArea();
                const diff = bS - aS;
                return diff;
              });
            }
          });
        }
      }
    }
  }
}

const ManufacturingFeatures = observer(function _ManufacturingFeatures() {
  const clientStore = useClientStore();
  const viewerControl = useViewerControl();

  // get the inspected body from current camera
  if (!viewerControl?.viewer) return null;

  const settings = viewerControl?.viewer?.settings;
  const camera = settings?.CurrentCamera();
  const instance = camera?.GetInstance();
  const body = instance?.GetBody();
  if (!body) return null;

  const surfEnum = viewerControl?.viewer?.module.SurfaceType;
  const bodyGeom = new BodyGeometry(body, surfEnum);

  const { uiMode } = viewerControl;
  const showSurfaces = camera.useSurfaceColors;
  const showDetails = !showSurfaces && uiMode === UiModes.feature;
  const showThickness = !showSurfaces && uiMode === UiModes.wallFeature;

  // get the currently running DFM analysis from viewerControl
  const { dfmError, dfmStatus, isWaiting } = viewerControl.meshAnalysis;

  // array of feature types
  const featEnum = viewerControl?.viewer?.module.TopoFeatureType;
  const featTypes = [
    -1, // wall offset pairs
    featEnum.TopoFeatureHole,
    featEnum.TopoFeatureSlot,
    featEnum.TopoFeatureBoss,
    featEnum.TopoFeatureGusset,
    featEnum.TopoFeatureNotch,
    featEnum.TopoFeatureCavity,
    featEnum.TopoFeaturePocket,
  ];

  /*
    effects
  */

  // mount: request DFM analysis
  useEffect(() => {
    if (body) {
      if (!body.isReadyForDfmCutoff) {
        // we need to request a DFM analysis
        viewerControl.RequestDfmAnalysis(body);
      }
    }
  }, []);

  /*
    render
  */

  const handleCloseBtn = () => {
    viewerControl.UpdateUiMode(UiModes.collapsed);
  };

  const info = () => {
    if (dfmError) {
      return 'Sorry, an error occurred';
    }

    if (isWaiting) {
      if (
        dfmStatus === DfmStatus.SUBSCRIBED ||
        dfmStatus === DfmStatus.REQUESTED
      ) {
        return 'Preparing the manufacturing features for your part...';
      }
      if (
        dfmStatus === DfmStatus.FINISHED ||
        dfmStatus === DfmStatus.FETCHING
      ) {
        return 'Loading manufacturing features...';
      }
      return 'Preparing the manufacturing features for your part...';
    }

    return null;
  };

  const tabHeight = 32;

  return (
    <FeaturesViewportWrapper tabHeight={tabHeight}>
      {clientStore.isMobile && (
        <>
          <Box
            p={2}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <Heading size='medium'>Feature browser</Heading>
            <SvgIcon icon={<Close />} size='large' onClick={handleCloseBtn} />
          </Box>
          <Divisor sx={{ marginBottom: 8 }} />
        </>
      )}

      {/* selection mode */}
      {!isWaiting && (
        <Navigation tabHeight={tabHeight}>
          <RowWrapper tabHeight={tabHeight}>
            <NavItem
              key='details'
              tabHeight={tabHeight}
              isSelected={showDetails}
              onClick={() => {
                camera.useSurfaceColors = false;
                if (viewerControl.uiMode !== UiModes.feature) {
                  viewerControl.UpdateUiMode(UiModes.feature);
                }
              }}
            >
              <Icon name='dashboard' viewBox='0 0 24 24' size='medium' />
              <NameWrapper>{'Details'}</NameWrapper>
            </NavItem>
            <NavItem
              key='wall'
              isSelected={showThickness}
              onClick={() => {
                camera.useSurfaceColors = false;
                if (viewerControl.uiMode !== UiModes.wallFeature) {
                  viewerControl.UpdateUiMode(UiModes.wallFeature);
                }
              }}
            >
              <Icon name='settings' viewBox='0 0 24 24' size='medium' />
              <NameWrapper>{'Thickness'}</NameWrapper>
            </NavItem>
            <NavItem
              key='surface'
              isLast
              isSelected={showSurfaces}
              onClick={() => {
                if (body.isFaceted) {
                  if (viewerControl.uiMode !== UiModes.mesh) {
                    camera.useSurfaceColors = !camera.useSurfaceColors;
                    viewerControl.UpdateUiMode(UiModes.mesh);
                  }
                } else if (viewerControl.uiMode !== UiModes.geometry) {
                  camera.useSurfaceColors = !camera.useSurfaceColors;
                  viewerControl.UpdateUiMode(UiModes.geometry);
                }
              }}
            >
              <Icon name='explode_layers' viewBox='0 0 40 40' size='medium' />
              <NameWrapper>{'Surfaces'}</NameWrapper>
            </NavItem>
          </RowWrapper>
        </Navigation>
      )}

      {/* Loading/Error... */}
      {info() && (
        <Box ml={1} mt={1} mb={1} sx={{ display: 'flex' }}>
          <SvgIcon icon={<InfoIcon />} size='medium' />
          <Box ml={2}>{info()}</Box>
        </Box>
      )}

      {/* Feature list */}
      {!showSurfaces && (
        <Box
          sx={{
            overflowY: 'auto',
            position: 'relative',
            top: '0',
            bottom: '0',
          }}
        >
          {/* for each feature group */}
          {featTypes.map((featType, ix) => {
            const featGroup = new BodyFeatureGroup(body, featType, featEnum);
            const { title, featCount, offsetPairCount, features, offsetPairs } =
              featGroup;
            if (!featGroup) return null;

            const featIcon = getFeatureIcon(featType, featEnum);
            if (showDetails && featCount > 0) {
              return (
                <AccordionItem
                  key={`featGroup_${ix}`}
                  icon={featIcon}
                  iconSize='xlarge'
                  divisor
                  title={title}
                  subTitle={`(${featCount})`}
                >
                  {/* for each feature */}
                  <Box>
                    {features.map((feat, key) => (
                      <Feature
                        key={`feat_${ix}_${key}`}
                        feature={feat}
                        ix={key}
                        icon={featIcon}
                      />
                    ))}
                  </Box>
                </AccordionItem>
              );
            }
            if (showThickness && offsetPairCount > 0) {
              return (
                <AccordionItem
                  key={`featGroup_${ix}`}
                  icon={featIcon}
                  iconSize='xlarge'
                  divisor
                  title={title}
                  subTitle={`(${offsetPairCount})`}
                >
                  {/* for each pairOffset */}
                  <Box>
                    {offsetPairs.map((offsetPair, key) => (
                      <WallOffsetPair
                        key={`feat_${ix}_${key}`}
                        offsetPair={offsetPair}
                        ix={key}
                        icon={featIcon}
                      />
                    ))}
                  </Box>
                </AccordionItem>
              );
            }
            return null;
          })}
        </Box>
      )}

      {/* Surface list */}
      {showSurfaces && (
        <Box
          sx={{
            overflowY: 'auto',
            position: 'relative',
            top: '0',
            bottom: '0',
          }}
        >
          {/* for each surface type */}
          {[...bodyGeom.faces.keys()].map((faceType, ix) => {
            const faceLabel = getSurfaceLabel(faceType, surfEnum);
            const faceIcon = getSurfaceIcon(faceType, surfEnum);
            const faceLabelColor = getSurfaceLabelColor(faceType, surfEnum);
            const faces = bodyGeom.faces.get(faceType);
            const faceCount = faces.length;
            if (faceCount > 0) {
              return (
                <AccordionItem
                  key={`faceGroup_${ix}`}
                  icon={faceIcon}
                  iconSize='xlarge'
                  divisor
                  title={faceLabel}
                  subTitle={`(${faceCount})`}
                  colorLabel={faceLabelColor}
                >
                  {/* for each face */}
                  <Box>
                    {faces.map((face, key) => (
                      <Face
                        key={`face_${faceType}_${key}`}
                        face={face}
                        ix={key}
                        icon={faceIcon}
                      />
                    ))}
                  </Box>
                </AccordionItem>
              );
            }
            return null;
          })}
        </Box>
      )}
    </FeaturesViewportWrapper>
  );
});

export default ManufacturingFeatures;
